import React, { useEffect, useState } from "react";
import { X, RotateCcw, Save, Trash2, Clock, FileDown, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatRelative } from "@/lib/utils";

/**
 * Modale de gestion des snapshots (sauvegardes) de l'horaire d'un événement.
 * Permet de créer un snapshot manuel, de restaurer un snapshot précédent
 * (avec ou sans conservation des assignations actuelles), ou de supprimer
 * une sauvegarde.
 */
export default function SnapshotsModal({ eventId, eventName, onClose, onRestored }) {
  const [loading, setLoading] = useState(true);
  const [snaps, setSnaps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [maxSnaps, setMaxSnaps] = useState(10);
  // Restore confirmation dialog
  const [confirming, setConfirming] = useState(null); // snapshot object
  const [keepAssignments, setKeepAssignments] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/events/${eventId}/snapshots`);
      setSnaps(data.snapshots || []);
      if (data.max) setMaxSnaps(data.max);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const createManual = async () => {
    setBusy(true);
    try {
      await api.post(`/events/${eventId}/snapshots`, { reason: "Manuel" });
      toast.success("Sauvegarde créée");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const askRestore = (snap) => {
    setConfirming(snap);
    setKeepAssignments(false);
  };

  const doRestore = async () => {
    if (!confirming) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/events/${eventId}/snapshots/${confirming.id}/restore`,
        { keep_current_assignments: keepAssignments }
      );
      toast.success(`Restauré · ${data.game_count} partie${data.game_count > 1 ? "s" : ""}`);
      setConfirming(null);
      onRestored && onRestored();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (snap) => {
    if (!window.confirm(`Supprimer la sauvegarde « ${snap.reason} » du ${new Date(snap.created_at).toLocaleString("fr-CA")} ?`)) return;
    setBusy(true);
    try {
      await api.delete(`/events/${eventId}/snapshots/${snap.id}`);
      toast.success("Sauvegarde supprimée");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  // Télécharge la sauvegarde côté client sous forme de fichier JSON.
  // L'utilisateur peut ainsi conserver une copie locale en cas de problème serveur.
  const doDownload = async (snap) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/events/${eventId}/snapshots/${snap.id}/download`);
      const blob = new Blob([JSON.stringify(data.snapshot, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date(snap.created_at).toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const safeName = (eventName || "evenement").replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 40);
      a.download = `sauvegarde_${safeName}_${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Sauvegarde téléchargée");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  // Télécharge un instantané de l'événement COURANT (sans passer par la DB des snapshots).
  // Utile juste avant un auto-assign pour avoir un fichier local en secours.
  const doDownloadCurrent = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/events/${eventId}`);
      const payload = {
        exported_at: new Date().toISOString(),
        event: data.event,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const safeName = (eventName || "evenement").replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 40);
      a.download = `export_horaire_${safeName}_${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Horaire exporté");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="snapshots-modal"
    >
      <div
        className="rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 relative"
        style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-[var(--surface-3)]"
          aria-label="Fermer"
          data-testid="snapshots-close-btn"
        >
          <X size={18} strokeWidth={2.2} />
        </button>

        <div className="label mb-1">Sauvegardes</div>
        <h3 className="text-xl font-bold tracking-tight mb-1" data-testid="snapshots-title">
          « {eventName || "événement"} »
        </h3>
        <p className="text-xs text-[var(--muted)] mb-4">
          Une sauvegarde est créée automatiquement avant chaque import et chaque opération
          destructive. Tu peux aussi en créer une manuellement à n&apos;importe quel moment.
          {" "}Max {maxSnaps} sauvegardes par événement (les plus vieilles sont supprimées
          automatiquement).
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={createManual}
            disabled={busy}
            className="btn-primary inline-flex items-center gap-1.5"
            data-testid="snapshots-create-btn"
          >
            <Save size={14} strokeWidth={2.4} /> Sauvegarder maintenant
          </button>
          <button
            type="button"
            onClick={doDownloadCurrent}
            disabled={busy}
            className="btn-secondary inline-flex items-center gap-1.5"
            data-testid="snapshots-download-current-btn"
            title="Télécharge un fichier JSON de l'horaire courant (à conserver localement en cas de problème)"
          >
            <Download size={14} strokeWidth={2.4} /> Télécharger l&apos;horaire actuel
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-[var(--muted)]">Chargement…</div>
        ) : snaps.length === 0 ? (
          <div className="rounded-xl p-4 text-sm text-[var(--muted)]" style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }} data-testid="snapshots-empty">
            Aucune sauvegarde pour cet événement. Crée-en une manuellement, ou elle sera créée
            automatiquement à la prochaine action destructive.
          </div>
        ) : (
          <ul className="space-y-3" data-testid="snapshots-list">
            {snaps.map((s) => (
              <li
                key={s.id}
                className="rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}
                data-testid={`snapshot-row-${s.id}`}
              >
                <div className="min-w-0 flex items-start gap-3">
                  <span
                    className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}
                  >
                    <FileDown size={14} strokeWidth={2.4} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-bold truncate" data-testid={`snapshot-reason-${s.id}`}>
                      {s.reason || "Manuel"}
                    </div>
                    <div className="text-xs text-[var(--muted)] flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                      <span className="inline-flex items-center gap-1">
                        <Clock size={11} strokeWidth={2.4} />
                        {formatRelative(s.created_at)}
                      </span>
                      <span>·</span>
                      <span>{s.game_count} partie{s.game_count > 1 ? "s" : ""}</span>
                      {s.created_by_email && (
                        <>
                          <span>·</span>
                          <span className="truncate">{s.created_by_email}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => doDownload(s)}
                    disabled={busy}
                    className="btn-ghost inline-flex items-center gap-1.5 text-xs"
                    data-testid={`snapshot-download-btn-${s.id}`}
                    title="Télécharger cette sauvegarde en JSON"
                  >
                    <Download size={12} strokeWidth={2.4} />
                  </button>
                  <button
                    type="button"
                    onClick={() => askRestore(s)}
                    disabled={busy}
                    className="btn-secondary inline-flex items-center gap-1.5 text-xs"
                    data-testid={`snapshot-restore-btn-${s.id}`}
                  >
                    <RotateCcw size={12} strokeWidth={2.4} /> Restaurer
                  </button>
                  <button
                    type="button"
                    onClick={() => doDelete(s)}
                    disabled={busy}
                    className="btn-ghost inline-flex items-center gap-1.5 text-xs"
                    data-testid={`snapshot-delete-btn-${s.id}`}
                    title="Supprimer cette sauvegarde"
                  >
                    <Trash2 size={12} strokeWidth={2.4} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center justify-end mt-5">
          <button onClick={onClose} className="btn-ghost" data-testid="snapshots-close-bottom-btn">
            Fermer
          </button>
        </div>
      </div>

      {confirming && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          data-testid="snapshot-restore-modal"
        >
          <div
            className="rounded-2xl max-w-md w-full p-6"
            style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}
          >
            <div className="label mb-1">Confirmation de restauration</div>
            <h4 className="font-serif text-2xl leading-tight mb-2">
              Restaurer cette sauvegarde&nbsp;?
            </h4>
            <p className="text-sm text-[var(--text)] mb-4">
              {confirming.reason} · {confirming.game_count} partie
              {confirming.game_count > 1 ? "s" : ""} ·{" "}
              {formatRelative(confirming.created_at)}.
              <br />
              <span className="text-xs text-[var(--muted)]">
                Avant la restauration, une nouvelle sauvegarde automatique de l&apos;état actuel
                sera créée pour permettre un retour en arrière.
              </span>
            </p>

            <label
              className="flex items-start gap-2 cursor-pointer mb-4 rounded-xl p-3"
              style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}
              data-testid="snapshot-keep-assignments-label"
            >
              <input
                type="checkbox"
                checked={keepAssignments}
                onChange={(e) => setKeepAssignments(e.target.checked)}
                className="mt-0.5 w-3.5 h-3.5"
                data-testid="snapshot-keep-assignments-input"
              />
              <span className="text-sm leading-snug">
                <span className="font-semibold">Conserver les assignations actuelles</span>
                <span className="block text-xs text-[var(--muted)] mt-0.5">
                  Restaure les parties (heures, surfaces, équipes…) MAIS garde les arbitres et
                  marqueurs actuels pour les parties qui existent encore. Si décoché, tout est
                  restauré tel quel (assignations incluses).
                </span>
              </span>
            </label>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirming(null)}
                className="btn-secondary"
                data-testid="snapshot-restore-cancel-btn"
                disabled={busy}
              >
                Annuler
              </button>
              <button
                onClick={doRestore}
                className="btn-primary"
                data-testid="snapshot-restore-confirm-btn"
                disabled={busy}
              >
                Restaurer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
